/**
 * @fileoverview React hooks — simulation engine, mission engine, overlays
 *
 * Architecture:
 *  - useSimulationEngine: owns rooms + params + clock. Each tick solves the
 *    coupled hydraulic network for the current TRV openings and integrates
 *    room temperatures. Derived display values (network, scores, alarms) are
 *    memoised pure-function results — no duplicated state.
 *  - useMissionEngine: 4C/ID learning-task state machine on top of the engine
 *    (scenario setup, live criteria evaluation, question tracking, stars,
 *    localStorage persistence).
 *  - useOverlay: single-active-overlay state.
 *
 * @module HydraulicBalancingTesting/hooks
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import {
  SIM,
  HEAT_PUMP,
  PUMP,
  OUTDOOR,
  createBuilding,
  createDefaultParams,
} from './constants';

import {
  solveNetwork,
  thermalStep,
  applyDesignCalculation,
  currentSupplyTemp,
  heatPumpCOP,
  mixedReturnTemp,
  balanceDeviations,
  balanceScore,
  comfortScore,
  efficiencyScore,
  toM3PerHour,
  clamp,
} from './physics';

import { MISSIONS, getMission, buildScenarioHelpers } from './missions';

// ============================================================================
// SIMULATION ENGINE
// ============================================================================

/** Fresh, design-annotated building. */
const freshBuilding = () => applyDesignCalculation(createBuilding());

/**
 * Runs a zero-length thermal step so live fields (flow, Treturn, qrad,
 * authority, velocity) are populated before the simulation has ever ticked.
 */
const populateLiveFields = (rooms, params) => {
  const network = solveNetwork(rooms, params.pumpHead, { forceTrvOpen: params.balancingMode, pipes: params.pipes });
  return thermalStep({
    rooms,
    network,
    tSupply: currentSupplyTemp(params),
    tOutdoor: params.tOutdoor,
    airChangeRate: params.airChangeRate,
    dt: 0,
    balancingMode: params.balancingMode,
  }).rooms;
};

export const useSimulationEngine = () => {
  const [params, setParams] = useState(createDefaultParams);
  const [rooms, setRooms] = useState(() => populateLiveFields(freshBuilding(), createDefaultParams()));
  const [isRunning, setIsRunning] = useState(false);
  const [speed, setSpeed] = useState(SIM.SPEEDS[1]);
  const [simTime, setSimTime] = useState(0);
  const [timeSinceChange, setTimeSinceChange] = useState(0);
  const [energy, setEnergy] = useState({ thermalJ: 0, electricJ: 0 });

  const timerRef = useRef(null);
  // refs so the interval callback always sees current values without re-arming
  const stateRef = useRef({ params, speed });
  stateRef.current = { params, speed };

  // --------------------------------------------------------------------------
  // Tick: solve network for current TRV state, integrate temperatures
  // --------------------------------------------------------------------------
  const tick = useCallback(() => {
    const { params: p, speed: spd } = stateRef.current;
    const dt = SIM.BASE_DT * spd;
    const tSupply = currentSupplyTemp(p);

    setRooms((prevRooms) => {
      const network = solveNetwork(prevRooms, p.pumpHead, {
        forceTrvOpen: p.balancingMode,
        pipes: p.pipes,
      });
      const { rooms: updated, totals } = thermalStep({
        rooms: prevRooms,
        network,
        tSupply,
        tOutdoor: p.tOutdoor,
        airChangeRate: p.airChangeRate,
        dt,
        balancingMode: p.balancingMode,
      });

      // energy bookkeeping
      const cop = heatPumpCOP(tSupply, p.tOutdoor);
      const electricHP = totals.qrad / Math.max(cop, 0.1);
      setEnergy((e) => ({
        thermalJ: e.thermalJ + totals.qrad * dt,
        electricJ: e.electricJ + (electricHP + network.pumpPower) * dt,
      }));

      // temperature history (one sample per tick)
      return updated.map((room) => ({
        ...room,
        history: [...room.history, room.T].slice(-SIM.HISTORY_LENGTH),
      }));
    });

    setSimTime((t) => t + dt);
    setTimeSinceChange((t) => t + dt);
  }, []);

  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(tick, SIM.TICK_MS);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRunning, tick]);

  // --------------------------------------------------------------------------
  // Derived values (pure recomputation — cheap)
  // --------------------------------------------------------------------------
  const derived = useMemo(() => {
    const tSupply = currentSupplyTemp(params);
    const network = solveNetwork(rooms, params.pumpHead, {
      forceTrvOpen: params.balancingMode,
      pipes: params.pipes,
    });
    const deviations = balanceDeviations(rooms, params.pumpHead, params.pipes);
    const cop = heatPumpCOP(tSupply, params.tOutdoor);
    const tReturn = mixedReturnTemp(rooms, tSupply);
    const totalFlowM3h = toM3PerHour(network.totalFlow);

    const alarms = {
      minFlow: totalFlowM3h < HEAT_PUMP.MIN_FLOW_M3H,
      noise: network.velocity.some((v) => v > 0.45),
    };

    const scores = {
      balance: balanceScore(deviations),
      comfort: comfortScore(rooms),
      efficiency: efficiencyScore(cop, params.tOutdoor, network.pumpPower),
    };

    const totalQrad = rooms.reduce((s, r) => s + r.qrad, 0);
    const spf = energy.electricJ > 0 ? energy.thermalJ / energy.electricJ : 0;

    return { tSupply, network, deviations, cop, tReturn, totalFlowM3h, alarms, scores, totalQrad, spf };
  }, [rooms, params, energy]);

  // --------------------------------------------------------------------------
  // Mutations (all reset the settle clock)
  // --------------------------------------------------------------------------
  const touch = useCallback(() => setTimeSinceChange(0), []);

  const setParam = useCallback((key, value) => {
    setParams((p) => ({ ...p, [key]: value }));
    touch();
  }, [touch]);

  const setRoomPreset = useCallback((roomIdx, presetIndex) => {
    setRooms((rs) => rs.map((r, i) => (i === roomIdx ? { ...r, presetIndex } : r)));
    touch();
  }, [touch]);

  /** Updates a room property and re-runs the design calculation. */
  const setRoomProp = useCallback((roomIdx, prop, value) => {
    setRooms((rs) => {
      const next = rs.map((r, i) => (i === roomIdx ? { ...r, [prop]: value } : r));
      return applyDesignCalculation(next, { resizeRadiators: false });
    });
    touch();
  }, [touch]);

  /** Sets one property on EVERY room (e.g. "apply this U-value to all"). */
  const setAllRoomsProp = useCallback((prop, value) => {
    setRooms((rs) =>
      applyDesignCalculation(
        rs.map((r) => ({ ...r, [prop]: value })),
        { resizeRadiators: false }
      )
    );
    touch();
  }, [touch]);

  /** Sets the valve preset of every room at once (e.g. from the optimizer). */
  const setAllPresets = useCallback((indices) => {
    setRooms((rs) => rs.map((r, i) => (indices[i] != null ? { ...r, presetIndex: indices[i] } : r)));
    touch();
  }, [touch]);

  /** Applies an optimization result: fixed supply temperature + per-room presets. */
  const applyOptimization = useCallback(({ tSupply, presets }) => {
    setParams((p) => ({ ...p, supplyMode: 'fixed', tSupplyFixed: tSupply }));
    setRooms((rs) => rs.map((r, i) => (presets[i] != null ? { ...r, presetIndex: presets[i] } : r)));
    touch();
  }, [touch]);

  /** Loads a complete scenario (mission setup or sandbox reset). */
  const loadScenario = useCallback(({ params: p = {}, rooms: r = null, running = false } = {}) => {
    const merged = { ...createDefaultParams(), ...p };
    setParams(merged);
    setRooms(populateLiveFields(r || freshBuilding(), merged));
    setSimTime(0);
    setTimeSinceChange(0);
    setEnergy({ thermalJ: 0, electricJ: 0 });
    setIsRunning(running);
  }, []);

  return {
    // state
    params,
    rooms,
    isRunning,
    speed,
    simTime,
    timeSinceChange,
    energy,
    derived,
    // controls
    setIsRunning,
    setSpeed,
    setParam,
    setRoomPreset,
    setRoomProp,
    setAllRoomsProp,
    setAllPresets,
    applyOptimization,
    loadScenario,
  };
};

// ============================================================================
// MISSION ENGINE (4C/ID learning tasks)
// ============================================================================

const STORAGE_KEY = 'hbt-mission-progress-v1';

const loadProgress = () => {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

export const useMissionEngine = (engine, { onOverlayChange } = {}) => {
  /** null = sandbox mode */
  const [activeMissionId, setActiveMissionId] = useState(null);
  const [questionState, setQuestionState] = useState({});
  const [progress, setProgress] = useState(loadProgress);
  const [celebrated, setCelebrated] = useState(false);

  const activeMission = activeMissionId ? getMission(activeMissionId) : null;

  const persistProgress = useCallback((next) => {
    setProgress(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* private mode — keep in memory */
    }
  }, []);

  // --------------------------------------------------------------------------
  // Start / leave missions
  // --------------------------------------------------------------------------
  const startMission = useCallback((missionId) => {
    const mission = getMission(missionId);
    if (!mission) return;
    const helpers = buildScenarioHelpers(freshBuilding);
    const scenario = mission.setup(helpers);
    engine.loadScenario(scenario);
    setQuestionState({});
    setCelebrated(false);
    setActiveMissionId(missionId);
    if (onOverlayChange) onOverlayChange(mission.scaffold.defaultOverlay || 'none');
  }, [engine, onOverlayChange]);

  const restartMission = useCallback(() => {
    if (activeMissionId) startMission(activeMissionId);
  }, [activeMissionId, startMission]);

  const exitToSandbox = useCallback(() => {
    setActiveMissionId(null);
    setQuestionState({});
    setCelebrated(false);
    engine.loadScenario({ running: false });
    if (onOverlayChange) onOverlayChange('none');
  }, [engine, onOverlayChange]);

  // --------------------------------------------------------------------------
  // Questions (M1)
  // --------------------------------------------------------------------------
  const answerQuestion = useCallback((questionId, optionIndex) => {
    if (!activeMission) return;
    const question = (activeMission.questions || []).find((q) => q.id === questionId);
    if (!question) return;
    setQuestionState((qs) => {
      const prev = qs[questionId] || { attempts: 0, correct: false };
      if (prev.correct) return qs;
      return {
        ...qs,
        [questionId]: {
          attempts: prev.attempts + 1,
          correct: optionIndex === question.correct,
          chosen: optionIndex,
        },
      };
    });
  }, [activeMission]);

  // --------------------------------------------------------------------------
  // Live evaluation
  // --------------------------------------------------------------------------
  const snapshot = useMemo(() => ({
    rooms: engine.rooms,
    deviations: engine.derived.deviations,
    params: engine.params,
    supplyTemp: engine.derived.tSupply,
    cop: engine.derived.cop,
    network: engine.derived.network,
    alarms: engine.derived.alarms,
    scores: engine.derived.scores,
    timeSinceChange: engine.timeSinceChange,
    questionState,
  }), [engine.rooms, engine.derived, engine.params, engine.timeSinceChange, questionState]);

  const evaluation = useMemo(
    () => (activeMission ? activeMission.evaluate(snapshot) : null),
    [activeMission, snapshot]
  );

  // Persist best result when a mission passes
  useEffect(() => {
    if (!activeMission || !evaluation?.passed || celebrated) return;
    setCelebrated(true);
    const prev = progress[activeMission.id] || { stars: 0 };
    if (evaluation.stars > prev.stars || !prev.completed) {
      persistProgress({
        ...progress,
        [activeMission.id]: { completed: true, stars: Math.max(evaluation.stars, prev.stars || 0) },
      });
    }
  }, [activeMission, evaluation, celebrated, progress, persistProgress]);

  // --------------------------------------------------------------------------
  // Scaffold helpers for the UI
  // --------------------------------------------------------------------------
  const scaffold = activeMission ? activeMission.scaffold : null;

  const isPresetEditable = useCallback((roomIdx) => {
    if (!scaffold) return true; // sandbox
    return scaffold.presetsEditable.includes(roomIdx);
  }, [scaffold]);

  const isSystemEditable = useCallback((key) => {
    if (!scaffold) return true; // sandbox
    if (key === 'balancingMode') return !!scaffold.balancingToggle;
    return scaffold.systemEditable.includes(key);
  }, [scaffold]);

  const showTargets = scaffold ? scaffold.showTargets : true;

  return {
    missions: MISSIONS,
    activeMission,
    progress,
    questionState,
    evaluation,
    celebrated,
    startMission,
    restartMission,
    exitToSandbox,
    answerQuestion,
    isPresetEditable,
    isSystemEditable,
    showTargets,
  };
};

// ============================================================================
// OVERLAY STATE
// ============================================================================

export const OVERLAYS = [
  { key: 'deviation', label: 'Flow deviation' },
  { key: 'temperature', label: 'Room temperature' },
  { key: 'return', label: 'Return temperature' },
  { key: 'heatflux', label: 'Heat losses' },
];

export const useOverlay = () => {
  const [overlay, setOverlay] = useState('none');
  const toggleOverlay = useCallback((key) => {
    setOverlay((cur) => (cur === key ? 'none' : key));
  }, []);
  return { overlay, setOverlay, toggleOverlay };
};

// ============================================================================
// SMALL UTILITIES
// ============================================================================

/** Formats simulated seconds as "3 h 24 min". */
export const formatSimTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m} min`;
  return `${h} h ${String(m).padStart(2, '0')} min`;
};

/** J → kWh string */
export const joulesToKWh = (j) => (j / 3.6e6).toFixed(2);

export { clamp, OUTDOOR, PUMP };
